import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Toaster } from '@/components/ui/sonner';
import LaunchPromoBanner from '@/components/LaunchPromoBanner';

import Landing from '@/pages/Landing';
import About from '@/pages/About';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import SignupSuccess from '@/pages/SignupSuccess';
import ChangePassword from '@/pages/ChangePassword';

import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminBailleurs from '@/pages/admin/AdminBailleurs';
import AdminBailleurDetail from '@/pages/admin/AdminBailleurDetail';
import AdminLocataires from '@/pages/admin/AdminLocataires';
import AdminAgents from '@/pages/admin/AdminAgents';
import AdminAgentDetail from '@/pages/admin/AdminAgentDetail';
import AdminAbonnements from '@/pages/admin/AdminAbonnements';
import AdminCatalogue from '@/pages/admin/AdminCatalogue';
import AdminAudit from '@/pages/admin/AdminAudit';
import Privacy from '@/pages/legal/Privacy';
import CGU from '@/pages/legal/CGU';

import AgentDashboard from '@/pages/agent/AgentDashboard';
import AgentBailleurs from '@/pages/agent/AgentBailleurs';
import AgentBailleurDetail from '@/pages/agent/AgentBailleurDetail';
import AgentEcheances from '@/pages/agent/AgentEcheances';
import AgentProfile from '@/pages/agent/AgentProfile';

import BailleurDashboard from '@/pages/bailleur/BailleurDashboard';
import BailleurLocataires from '@/pages/bailleur/BailleurLocataires';
import BailleurLocataireDetail from '@/pages/bailleur/BailleurLocataireDetail';
import BailleurPaiements from '@/pages/bailleur/BailleurPaiements';
import BailleurCalendrier from '@/pages/bailleur/BailleurCalendrier';
import BailleurAbonnement from '@/pages/bailleur/BailleurAbonnement';

import LocataireDashboard from '@/pages/locataire/LocataireDashboard';
import { PWAInstallBanner } from '@/components/PWAInstallBanner';
import { Loader } from '@/components/Loader';

function PublicLanding() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F6FF]">
        <Loader variant="full" />
      </div>
    );
  }
  if (user) {
    if (user.role === 'platform_owner' || user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'agent') return <Navigate to="/agent" replace />;
    if (user.role === 'bailleur') return <Navigate to="/bailleur" replace />;
    if (user.role === 'locataire') return <Navigate to="/locataire" replace />;
  }
  return <Landing />;
}

function LandingPromoCounter() {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (location.pathname !== '/' || loading || user) return null;
  return (
    <div className="pointer-events-none fixed inset-x-3 bottom-3 z-50 mx-auto max-w-xl sm:bottom-5">
      <div className="pointer-events-auto shadow-2xl shadow-slate-900/20">
        <LaunchPromoBanner compact />
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicLanding />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/connexion/:role" element={<Login />} />
          <Route path="/inscription" element={<Signup />} />
          <Route path="/inscription/succes" element={<SignupSuccess />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

          <Route path="/admin" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/bailleurs" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminBailleurs /></ProtectedRoute>} />
          <Route path="/admin/bailleurs/:id" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminBailleurDetail /></ProtectedRoute>} />
          <Route path="/admin/locataires" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminLocataires /></ProtectedRoute>} />
          <Route path="/admin/agents" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminAgents /></ProtectedRoute>} />
          <Route path="/admin/agents/:id" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminAgentDetail /></ProtectedRoute>} />
          <Route path="/admin/abonnements" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminAbonnements /></ProtectedRoute>} />
          <Route path="/admin/catalogue" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminCatalogue /></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute roles={['platform_owner', 'admin']}><AdminAudit /></ProtectedRoute>} />

          <Route path="/politique-confidentialite" element={<Privacy />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/cgu" element={<CGU />} />

          <Route path="/agent" element={<ProtectedRoute roles={['agent']}><AgentDashboard /></ProtectedRoute>} />
          <Route path="/agent/bailleurs" element={<ProtectedRoute roles={['agent']}><AgentBailleurs /></ProtectedRoute>} />
          <Route path="/agent/bailleurs/:id" element={<ProtectedRoute roles={['agent']}><AgentBailleurDetail /></ProtectedRoute>} />
          <Route path="/agent/echeances" element={<ProtectedRoute roles={['agent']}><AgentEcheances /></ProtectedRoute>} />
          <Route path="/agent/profil" element={<ProtectedRoute roles={['agent']}><AgentProfile /></ProtectedRoute>} />

          <Route path="/bailleur" element={<ProtectedRoute roles={['bailleur']}><BailleurDashboard /></ProtectedRoute>} />
          <Route path="/bailleur/locataires" element={<ProtectedRoute roles={['bailleur']}><BailleurLocataires /></ProtectedRoute>} />
          <Route path="/bailleur/locataires/:id" element={<ProtectedRoute roles={['bailleur']}><BailleurLocataireDetail /></ProtectedRoute>} />
          <Route path="/bailleur/paiements" element={<ProtectedRoute roles={['bailleur']}><BailleurPaiements /></ProtectedRoute>} />
          <Route path="/bailleur/calendrier" element={<ProtectedRoute roles={['bailleur']}><BailleurCalendrier /></ProtectedRoute>} />
          <Route path="/bailleur/abonnement" element={<ProtectedRoute roles={['bailleur']}><BailleurAbonnement /></ProtectedRoute>} />
          <Route path="/abonnement/upgrade" element={<ProtectedRoute roles={['bailleur']}><BailleurAbonnement /></ProtectedRoute>} />
          <Route path="/abonnement/succes" element={<ProtectedRoute roles={['bailleur']}><SignupSuccess upgrade /></ProtectedRoute>} />

          <Route path="/locataire" element={<ProtectedRoute roles={['locataire']}><LocataireDashboard /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <LandingPromoCounter />
        <Toaster position="top-right" richColors closeButton />
        <PWAInstallBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
